import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const CultisgoLandingPage = ({ branding = {} }) => {
  const navigate = useNavigate();
  const { primaryColor = '#7c3aed', accentColor = '#22d3ee', companyName = 'CultisGo' } = branding;

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleLogin = (type) => {
    switch (type) {
      case 'paas': navigate('/paas-admin/login'); break;
      case 'saas': navigate('/saas-admin/login'); break;
      case 'client': navigate('/client-admin/login'); break;
      case 'app': navigate('/login'); break;
      default: navigate('/login');
    }
  };

  const gradientStyle = (dir = '135deg') => ({
    background: `linear-gradient(${dir}, ${primaryColor} 0%, ${accentColor} 100%)`
  });

  return (
    <div className="min-h-screen bg-annona-dark text-white font-sans">
      <style>{`
        @keyframes gradient-shift { 0%,100%{background-position:0%50%}50%{background-position:100%50%} }
        @keyframes float { 0%,100%{transform:translateY(0)}50%{transform:translateY(-20px)} }
        @keyframes glow-pulse { 0%,100%{box-shadow:0 0 20px ${primaryColor}66}50%{box-shadow:0 0 40px ${primaryColor}aa,0 0 60px ${accentColor}66} }
        @keyframes border-rotate { 0%{background:linear-gradient(45deg,${primaryColor},${accentColor},${primaryColor})}100%{background:linear-gradient(405deg,${primaryColor},${accentColor},${primaryColor})} }
        @keyframes shine { 0%{transform:translateX(-100%) rotate(45deg)}100%{transform:translateX(100%) rotate(45deg)} }
        .text-gradient{background:linear-gradient(90deg,${accentColor},#a78bfa,#c084fc,${accentColor});background-size:300% 100%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:gradient-shift 4s ease infinite}
        .glow-pulse{animation:glow-pulse 2s ease-in-out infinite}
        .float-slow{animation:float 8s ease-in-out infinite}
        .hero-gradient{background:linear-gradient(135deg,#050810 0%,#0a0f2e 30%,${primaryColor} 60%,${primaryColor}Light 100%);background-size:200% 200%;animation:gradient-shift 10s ease infinite}
        .quantum-gradient{background:linear-gradient(135deg,#050810 0%,#0a0f2e 30%,#1e1b4b 60%,#0a0f2e 100%);background-size:200% 200%;animation:gradient-shift 15s ease infinite}
        .grid-bg{background-image:linear-gradient(rgba(108,58,232,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(108,58,232,0.05) 1px,transparent 1px);background-size:50px 50px}
        .card-glow:hover{box-shadow:0 0 30px ${primaryColor}88,0 0 60px ${accentColor}44}
        .service-card-animated{transition:all 0.4s cubic-bezier(0.175,0.885,0.32,1.275)}
        .service-card-animated:hover{transform:translateY(-15px) scale(1.02);box-shadow:0 25px 50px -12px ${primaryColor}66}
        .shine::after{content:'';position:absolute;top:-50%;left:-50%;width:200%;height:200%;background:linear-gradient(to right,transparent,rgba(255,255,255,0.1),transparent);transform:rotate(45deg);animation:shine 3s infinite}
      `}</style>

      {/* Header */}
      <header className={`fixed top-0 w-full z-50 bg-annona-dark/95 backdrop-blur-md border-b border-purple-500/30 transition-all ${scrolled ? 'shadow-lg' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="#" className="flex items-center space-x-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center glow-pulse" style={gradientStyle()}>
                <span className="text-xl font-bold text-white">A</span>
              </div>
              <span className="text-xl font-bold text-gradient">{companyName}</span>
            </a>
            <nav className="hidden md:flex items-center space-x-8">
              {['home', 'platform', 'services', 'products', 'about', 'contact'].map(item => (
                <a key={item} href={`#${item}`} onClick={(e) => { e.preventDefault(); scrollTo(item); }}
                  className="text-gray-300 hover:text-white transition-colors capitalize">{item}</a>
              ))}
            </nav>
            <button onClick={() => scrollTo('login')}
              className="px-6 py-2.5 rounded-lg font-medium hover:opacity-90 transition-opacity glow-pulse"
              style={gradientStyle()}>
              Login
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero-gradient min-h-screen pt-20 relative overflow-hidden grid-bg">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl glow-pulse opacity-30" style={{ background: primaryColor }}></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: accentColor }}></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full blur-3xl opacity-15 bg-pink-500"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative z-10">
          <div className="grid lg:grid-cols-1 gap-12 items-center">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 rounded-full border mb-6" style={{ background: `${primaryColor}20`, borderColor: `${primaryColor}50` }}>
                <span className="w-2 h-2 rounded-full animate-pulse mr-2" style={{ background: accentColor }}></span>
                <span className="text-sm text-gray-300">Quantum Computing + FinTech</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Next Generation<br />
                <span className="text-gradient">Quantum FinTech</span><br />
                Platform
              </h1>
              <p className="text-lg text-center text-gray-300 mb-8 max-w-xl mx-auto">
                {companyName} combines quantum computing technologies with financial technology to provide advanced platforms for risk management, portfolio optimization, regulatory compliance, banking APIs, and cybersecurity solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center ">
                <a href="#platform" onClick={(e) => { e.preventDefault(); scrollTo('platform'); }}
                  className="px-8 py-3.5 rounded-lg font-semibold text-white hover:opacity-90 transition-opacity text-center glow-pulse"
                  style={gradientStyle()}>
                  Explore Platform
                </a>
                <a href="#login" onClick={(e) => { e.preventDefault(); scrollTo('login'); }}
                  className="border px-8 py-3.5 rounded-lg font-semibold text-white hover:bg-white/10 transition-colors text-center">
                  Login
                </a>
              </div>
            </div>
            {/* Login Box */}
            {/* <div id="login" className="flex justify-center lg:justify-end">
              <div className="bg-annona-navy/80 backdrop-blur-lg rounded-2xl p-8 w-full max-w-md border border-purple-500/50"
                style={{ boxShadow: `0 0 60px ${primaryColor}66, 0 0 100px ${accentColor}33` }}>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={gradientStyle()}>
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold">Welcome Back</h3>
                  <p className="text-gray-400 text-sm mt-1">Sign in to your account</p>
                </div>
                <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); handleLogin('app'); }}>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email / Username</label>
                    <input type="text" className="w-full px-4 py-3 rounded-lg bg-annona-dark border text-white focus:border-purple-500 focus:outline-none transition-colors"
                      placeholder="Enter your email" style={{ borderColor: `${primaryColor}50` }} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                    <input type="password" className="w-full px-4 py-3 rounded-lg bg-annona-dark border text-white focus:border-purple-500 focus:outline-none transition-colors"
                      placeholder="Enter your password" style={{ borderColor: `${primaryColor}50` }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center">
                      <input type="checkbox" className="w-4 h-4 rounded bg-annona-dark border-purple-500/30" style={{ accentColor }} />
                      <span className="ml-2 text-sm text-gray-300">Remember me</span>
                    </label>
                    <a href="#" className="text-sm hover:underline" style={{ color: accentColor }}>Forgot Password?</a>
                  </div>
                  <button type="submit" className="w-full py-3 rounded-lg font-semibold text-white hover:opacity-90 transition-opacity" style={gradientStyle()}>
                    Sign In
                  </button>
                </form>
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-400">
                    Don't have an account? <a href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('contact'); }} className="hover:underline" style={{ color: accentColor }}>Contact Us</a>
                  </p>
                </div>
              </div>
            </div> */}
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Platform Section */}
      <section id="platform" className="quantum-gradient py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: `${primaryColor}20`, color: accentColor }}>
              Our Platform
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Introducing <span className="text-gradient">{companyName}</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Platform-as-a-Service for fintech companies. Leveraging quantum computing to deliver next-generation financial solutions.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '📊', title: 'Risk Management', desc: 'Advanced quantum-powered risk analysis and prediction models.' },
              { icon: '📈', title: 'Portfolio Optimization', desc: 'Quantum algorithms for optimal portfolio allocation.' },
              { icon: '💹', title: 'Financial Analytics', desc: 'Real-time analytics and insights powered by quantum computing.' },
              { icon: '🛡️', title: 'Regulatory Monitoring', desc: 'Automated compliance monitoring with real-time alerts.' },
              { icon: '🔌', title: 'Banking APIs', desc: 'Seamless integration with banking systems through secure APIs.' },
              { icon: '🔐', title: 'Cybersecurity', desc: 'Quantum-resistant security protocols.' }
            ].map((item, i) => (
              <div key={i} className="bg-annona-navy/50 rounded-xl p-6 border card-glow service-card-animated"
                style={{ borderColor: `${primaryColor}33` }}>
                <div className="w-12 h-12 rounded-lg mb-4 flex items-center justify-center glow-pulse" style={{ background: `${primaryColor}33` }}>
                  <span className="text-2xl">{item.icon}</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 md:py-32 bg-annona-navy/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: `${primaryColor}20`, color: accentColor }}>Our Services</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Comprehensive <span className="text-gradient">Solutions</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">End-to-end technology services powered by quantum computing for the financial industry.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { abbr: 'WaaS', name: 'WealthTech as a Service', desc: 'Complete wealth management solutions.' },
              { abbr: 'RaaS', name: 'RegTech as a Service', desc: 'Automated compliance and regulatory reporting.' },
              { abbr: 'IaaS', name: 'InsurTech as a Service', desc: 'Digital insurance with quantum risk assessment.' },
              { abbr: 'BaaS', name: 'Banking as a Service', desc: 'Digital banking infrastructure.' },
              { abbr: 'PaaS', name: 'Payments as a Service', desc: 'Secure payment processing.' },
              { abbr: 'CSaaS', name: 'Cybersecurity as a Service', desc: 'Enterprise-grade quantum security.' }
            ].map((s, i) => (
              <div key={i} className="bg-gradient-to-br from-annona-navy to-annona-dark rounded-xl p-6 border card-glow service-card-animated"
                style={{ borderColor: `${primaryColor}33`, animationDelay: `${i * 0.5}s` }}>
                <div className="w-14 h-14 rounded-xl mb-4 flex items-center justify-center float-slow" style={gradientStyle()}>
                  <span className="text-2xl font-bold text-white">{s.abbr}</span>
                </div>
                <h3 className="text-xl font-semibold mb-1">{s.abbr}</h3>
                <h4 className="text-sm font-medium mb-3" style={{ color: accentColor }}>{s.name}</h4>
                <p className="text-gray-400 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="quantum-gradient py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: `${primaryColor}20`, color: accentColor }}>Our Products</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Innovative <span className="text-gradient">Solutions</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Cutting-edge products built on the {companyName} platform.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: 'AQ', title: companyName, desc: 'Flagship Quantum Computing powered fintech platform.', color: primaryColor },
              { icon: '💰', title: 'Dhanapurti', desc: 'Supply Chain Finance solution.', color: '#10b981' },
              { icon: '🏦', title: 'Tasthana', desc: 'Bank Deposit System.', color: '#0ea5e9' },
              { icon: '⚖️', title: "Advocate's Assistant", desc: 'Legal case management software.', color: '#f97316' },
              { icon: '❤️', title: 'Super Sana', desc: 'Health and wellness platform.', color: '#ec4899' }
            ].map((p, i) => (
              <div key={i} className="bg-annona-navy/70 rounded-2xl p-8 border card-glow service-card-animated group relative overflow-hidden"
                style={{ borderColor: `${primaryColor}50` }}>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(135deg, ${primaryColor}10, ${accentColor}05)` }}></div>
                <div className="w-16 h-16 rounded-xl mb-6 group-hover:scale-110 transition-transform glow-pulse flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${p.color}, ${accentColor})` }}>
                  {p.icon === 'AQ' ? <span className="text-2xl font-bold text-white">{p.icon}</span>
                    : <span className="text-3xl">{p.icon}</span>}
                </div>
                <h3 className="text-2xl font-bold mb-3">{p.title}</h3>
                <p className="text-gray-400 mb-4">{p.desc}</p>
                <a href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('contact'); }}
                  className="font-medium hover:underline" style={{ color: accentColor }}>Learn More →</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 md:py-32 bg-annona-navy/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: `${primaryColor}20`, color: accentColor }}>About Us</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Pioneers in <span className="text-gradient">Quantum FinTech</span></h2>
              <p className="text-gray-300 text-lg mb-6">
                {companyName} is a pioneer in Quantum Computing powered financial technology. We deliver innovative banking and financial technology solutions globally.
              </p>
              <p className="text-gray-400 mb-8">
                Our flagship platform combines quantum computing with cutting-edge fintech solutions for risk management, portfolio optimization, regulatory compliance, and cybersecurity.
              </p>
              <div className="flex flex-wrap gap-4">
                {['FinTech', 'Digital Banking', 'RegTech', 'WealthTech', 'InsurTech', 'LendTech', 'TradeTech'].map(tag => (
                  <div key={tag} className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: accentColor }}></div>
                    <span className="text-gray-300 text-sm">{tag}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl blur-2xl opacity-50" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}></div>
              <div className="relative bg-annona-navy/80 backdrop-blur rounded-2xl p-8 border" style={{ borderColor: `${primaryColor}80` }}>
                <div className="grid grid-cols-2 gap-6">
                  {[{ val: '45+', label: 'Happy Clients' }, { val: '50+', label: 'Technologies' },
                    { val: '50+', label: 'Employees' }, { val: '7+', label: 'Years Experience' }].map((s, i) => (
                    <div key={i} className="text-center p-6 bg-annona-dark/50 rounded-xl card-glow service-card-animated" style={{ borderRadius: '12px' }}>
                      <div className="text-4xl font-bold text-gradient mb-2 glow-pulse">{s.val}</div>
                      <div className="text-gray-400 text-sm">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="quantum-gradient py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: `${primaryColor}20`, color: accentColor }}>Contact Us</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Get in <span className="text-gradient">Touch</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Have questions? We'd love to hear from you.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              <div className="space-y-4">
                {[
                  { icon: '📍', title: 'Address', val: 'Bangalore, India' },
                  { icon: '📧', title: 'Email', val: `info@${companyName.toLowerCase()}.com` },
                  { icon: '📞', title: 'Phone', val: '+91 81 06 06 60 60' }
                ].map((c, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${primaryColor}33` }}>
                      <span className="text-xl">{c.icon}</span>
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">{c.title}</h4>
                      <p className="text-gray-400">{c.val}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-annona-navy/70 rounded-2xl p-8 border" style={{ borderColor: `${primaryColor}50` }}>
              <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                {[{ label: 'Name', type: 'text', ph: 'Your name' }, { label: 'Email', type: 'email', ph: 'Your email' }].map((f, i) => (
                  <div key={i}>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{f.label}</label>
                    <input type={f.type} className="w-full px-4 py-3 rounded-lg bg-annona-dark border text-white focus:border-purple-500 focus:outline-none transition-colors"
                      placeholder={f.ph} style={{ borderColor: `${primaryColor}50` }} />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                  <textarea rows="4" className="w-full px-4 py-3 rounded-lg bg-annona-dark border text-white focus:border-purple-500 focus:outline-none transition-colors resize-none"
                    placeholder="Your message" style={{ borderColor: `${primaryColor}50` }}></textarea>
                </div>
                <button type="submit" className="w-full py-3 rounded-lg font-semibold text-white hover:opacity-90 transition-opacity" style={gradientStyle()}>
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-annona-dark border-t border-purple-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <a href="#" className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={gradientStyle()}>
                  <span className="text-xl font-bold text-white">A</span>
                </div>
                <span className="text-xl font-bold text-gradient">{companyName}</span>
              </a>
              <p className="text-gray-400 text-sm mb-4 max-w-md">
                Pioneers in Quantum Computing powered financial technology platforms.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Industries</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                {['FinTech', 'Digital Banking', 'RegTech', 'WealthTech', 'InsurTech'].map(t => (
                  <li key={t}><a href="#" className="hover:text-white transition-colors">{t}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                {[companyName, 'Dhanapurti', 'Tasthana', "Advocate's Assistant", 'Super Sana'].map(p => (
                  <li key={p}><a href="#" className="hover:text-white transition-colors">{p}</a></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-purple-500/20 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">© 2026 Annona IT Solutions. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CultisgoLandingPage;